import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { translate } from '@jsverse/transloco';
import moment from 'moment';
import { DropzoneComponent } from 'ngx-dropzone-wrapper';
import { SubprojectQuery } from 'src/app/akita/subproject/state/subprojects.query';
import { ComplaintsService } from 'src/app/core/service/api/complaints.service';
import { InAppChatService } from 'src/app/core/service/api/in-app-chat.service';
import { MiddelsService } from 'src/app/core/service/api/middels.service';
import { GenericComponent } from 'src/app/core/util/abstract/generic-component';
import { showFeedbackError, showFeedbackRemoved, showFeedbackSaved } from 'src/app/core/util/notification';
import { Globals } from 'src/app/globals';
import { DocumentSidebarData } from 'src/app/shared/directive/document-sidebar.directive';
import { environment } from 'src/environments/environment';
import { DeletionModalComponent } from '../deletion-modal/deletion-modal.component';

@Component({
  selector: 'app-bouwapp-publish-modal',
  templateUrl: './bouwapp-publish-modal.component.html',
  styleUrls: ['./bouwapp-publish-modal.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BouwappPublishModalComponent extends GenericComponent implements OnInit {
  @ViewChild('dropzone', {static: false}) dropzone: DropzoneComponent;
  protected dropzoneTemplate = `<div class="mt-10">${translate('reusable.drop_email')}<br><i class="material-icons">cloud_upload</i></div>`;
  upload: any;
  public file: File = null;
  protected dropzoneConfig: any;
  public attachments:any[]=[];
  public entity:any;
  public type='update-attachment-file-path';
  public apiKey: string = environment.tinymce.apiKey;
  public tempdata:any[]=[];
  public bouwAppPublish:any;
  public dialogAttachments:any[]=[];
  public is_upload:boolean=false;
  public documentAttachment:boolean=false;
  /** Start of today – used as min date so past dates cannot be selected */
  public minDate: Date;
  /** Filter that disables all past dates in the datepicker. */
  public noPastDatesFilter = (d: Date | null): boolean => {
    if (d === null) return false;
    const min = this.minDate ?? this.getStartOfToday();
    return this.normalizeToStartOfDay(d).getTime() >= min.getTime();
  };
  constructor(
    private subProjectQuery: SubprojectQuery,
    private _middelService: MiddelsService,
    private cd: ChangeDetectorRef,
    private dialogRef: MatDialogRef<BouwappPublishModalComponent>,
    @Inject(MAT_DIALOG_DATA) public docSidebarData: DocumentSidebarData,
    public globals: Globals,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public _complainsServices:ComplaintsService,
    public _InAppChatService:InAppChatService,
    public _rout: Router,
    public dialog: MatDialog,
    ) {
      super();
    }

  ngOnInit() {
    this.minDate = this.getStartOfToday();
    this.initializeEntity();
    this.dropZone();
    this.projectAdd();
    this.bouwAppPublish=this.data.bouwapp;
    if(this.bouwAppPublish){
      if(!this.bouwAppPublish.code){
        if(this.bouwAppPublish.attachments.length > 0){
          this.bouwappAttachmentAdd();
        }
      this.entity=this.bouwAppPublish;
      this.entity.sendPush = this.entity.pushStatus==='scheduled'? true: false;
    }
      if(this.entity.attachments.length==0){
      this.documentAttachment=true;
      }
    }
    if(this.data.calender){
      if(this.data.calender.documents.length == 0){
        this.documentAttachment=true;
      }else{
        this.documentAttachment=false;
      }
    }
  }
  public initializeEntity(): void {
    const candidateDate = this.getCalendarPublicationDate();
    this.entity = {
      attachments: [],
      publicationDate: this.isTodayOrFuture(candidateDate) ? candidateDate : null,
      content: '',
      sendPush: true,
      poll: null,
      targets: [],
      visibleToTargetsOnly: false,
      project: ''
    };
  }

  private getCalendarPublicationDate(): string | undefined {
    const cal = this.data.calender;
    if (!cal?.contents) return undefined;
    return cal.metadata?.form?.date_type === 'activity'
      ? cal.contents.created_at
      : cal.contents.sent_date;
  }

  private getStartOfToday(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private normalizeToStartOfDay(d: Date): Date {
    const copy = new Date(d);
    copy.setHours(0, 0, 0, 0);
    return copy;
  }

  /** True if the date is today or in the future (start-of-day comparison). */
  private isTodayOrFuture(date: string | Date | null | undefined): boolean {
    if (!date) return false;
    return !moment(date).startOf('day').isBefore(moment().startOf('day'));
  }

  /** True if publication date is set and is today or in the future (valid for submit). */
  public isPublicationDateValid(): boolean {
    return !!this.entity?.publicationDate && this.isTodayOrFuture(this.entity.publicationDate);
  }

  /** True when a date is entered but it is in the past (for manual-typing validation). */
  public isPublicationDateInvalid(): boolean {
    return !!this.entity?.publicationDate && !this.isTodayOrFuture(this.entity.publicationDate);
  }

  /** True if content has actual text (HTML stripped); used for required content validation. */
  public isContentValid(): boolean {
    const raw = this.entity?.content;
    if (raw == null || typeof raw !== 'string') return false;
    const text = raw.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
    return text.length > 0;
  }

  /** True when both date and content are valid and primary actions can be enabled. */
  public canSubmit(): boolean {
    return this.isPublicationDateValid() && this.isContentValid();
  }

  /** Called when editor content changes; ensures button state updates with OnPush. */
  public onContentChange(): void {
    this.cd.markForCheck();
  }

  public submit(){
    if (!this.canSubmit()) {
      return;
    }
    const publishdate= moment(this.entity.publicationDate).set({
      hour:this.minDate.getHours(),
      minute:this.minDate.getMinutes(),
      second:this.minDate.getSeconds(),
    }).format();
    this.removalfunction();
    // this.attachments.forEach(element => {
    //   this.entity.attachments.push(element);
    // });
    this.entity.publicationDate=publishdate;
    this._middelService.createPublish(this.data.calender.contents.id,this.entity).subscribe(res=>{
      showFeedbackSaved();
      this.close('submit');
    })
  }
  public close(action=''): void {
    this.dialogRef.close(action);
  }
  public documentAttached(file:any){
    this.tempdata=file;
    const flag=[];
    this.entity.attachments.forEach(el=>{
      flag.push(el.fileName)
    });
    file.forEach(element => {
      if(element.type=='s3'){
        if(this.entity.attachments.length==0){
          this._InAppChatService.dialogItemsUpload(element.id,this.type).subscribe((res:any)=>{
            this.entity.attachments.push({
              fileName:element.name,
              filePath:res.file,
              is360:false,
              sortOrder: true,
              title:element.name
            })
          })
        }else{
           if(!flag.includes(element.name)){
            this._InAppChatService.dialogItemsUpload(element.id,this.type).subscribe((res:any)=>{
              this.entity.attachments.push({
                fileName:element.name,
                filePath:res.file,
                is360:false,
                sortOrder: true,
                title:element.name
              })
            })
           }
        }
      }
    });
  }
  public dropZone(){
    this.dropzoneConfig = {
      url: `${window.location.protocol }//${ window.location.host}`,
      uploadMultiple: false,
      previewsContainer: '.dropzone-previews'
    };
  }
  public DocumentUpLoad(file){
    this.file = file.target.files[0];
    this.updateDocumentUpload(this.file);
    this.cd.detectChanges();
  }
  public updateDocumentUpload(file){
    this.is_upload=true;
    this._InAppChatService.attachmentUpload(file,this.type).subscribe((res:any)=>{
      this.documentAttachment=false;
      this.updateUploadList(file,res);
      this.cd.detectChanges();
    },(error)=>{
      this.is_upload=false;
      showFeedbackError(error);
      this.cd.detectChanges();
    });
  }
  public updateUploadList(file,res){
      const filename=file.name;
      this.entity.attachments.push({
        fileName:filename,
        filePath:res.file,
        is360:false,
        sortOrder: true,
        title:filename
      });
      this.data.calender.documents.push({id:file.size,name:filename});
      this.dialogAttachments.push({id:file.size,name:filename});
      this.is_upload=false;
  }
  public bouwappAttachmentAdd(){
    const temp=[];
    this.data.calender.documents.forEach(el => {
        temp.push(el.name);
    });
    this.bouwAppPublish.attachments.forEach(element => {
      if(!temp.includes(element.title)){
        this.data.calender.documents.push({id:element.id,name:element.fileName});
      }
      this.dialogAttachments.push({id:element.id,name:element.fileName});
      this.cd.detectChanges();
    });
    this.data.calender.documents.forEach(ele => {
      this.dialogAttachments.forEach(el=>{
        if(ele.name==el.name){
          el.id=ele.id;
        }
      })
    });
  }
  public projectAdd(){
    const sub:any=this.subProjectQuery.getActive();
    if(this._rout.url.includes('sub')){
      if(sub.tenant_code){
        this.entity.project=`/api/projects/${sub.tenant_code}`;
      }else{
        this.entity.project=`/api/projects/${this.globals.projectConfigs['services.integration.bouwapp.project.code']}`;
      }
    }
    else{
      this.entity.project=`/api/projects/${this.globals.projectConfigs['services.integration.bouwapp.project.code']}`;
    }
  }
  public update(){
    if (!this.canSubmit()) {
      return;
    }
    const publishdate= moment(this.entity.publicationDate).set({
      hour:this.minDate.getHours(),
      minute:this.minDate.getMinutes(),
      second:this.minDate.getSeconds(),
    }).format();
    this.removalfunction();
    this.attachments.forEach(element => {
      this.entity.attachments.push(element);
    });
    this.entity.publicationDate=publishdate;
    this._middelService.publishUpdate(this.bouwAppPublish.id,this.entity).subscribe(res=>{
      showFeedbackSaved();
      this.close('update');
    })
  }
  public deleteP() {
    const dialogRef = this.dialog.open(DeletionModalComponent, {
      data: 'Weet u het zeker dat u de Update wilt verwijderen?'
    });

    dialogRef.disableClose = true;
    dialogRef.afterClosed().subscribe((remove: boolean) => {
      if (remove) {
        this.deletePublish();
      }
    });
  }
  public deletePublish(){
    this._middelService.publishDelete(this.data.calender.contents.id).subscribe(res=>{
      showFeedbackRemoved();
      this.close('delete');
    })
  }
  public removalfunction(){
    if(this.dialogAttachments.length==0){
      this.entity.attachments=[];
    }else{
      const temp=[];
      this.dialogAttachments.forEach(el=>{
        temp.push(el.name)
      });
      this.entity.attachments=this.entity.attachments.filter(element => {
        if(temp.includes(element.fileName)){
          return element;
        }
      });
    }
  }
}
